'use client';

import { AnimatedTitle } from '../../animated-title';

interface CommonHeroSectionProps {
  title: string;
  description: string;
  leftTop: string;
  rightTop: string;
  tags: string[];
  /** A line under the title: what the entry belongs to, or where it was. */
  subtitle?: string | null;
  /** Something to show behind the header, such as a live room. Turns the panel dark. */
  backdrop?: React.ReactNode;
}

/**
 * The head of a catalogue entry: what it is and when in the corners, its title, the line it
 * belongs under, its summary and tags. Words only: the entry's pictures belong to the page
 * below. Every part is left out when the entry does not have it.
 */
export const Common_HeroSection: React.FC<CommonHeroSectionProps> = ({
  title,
  description,
  tags,
  leftTop,
  rightTop,
  subtitle,
  backdrop,
}) => {
  const dark = !!backdrop;
  const label = `text-[10px] font-mono mb-2 ${dark ? 'text-white' : 'text-gray-900'}`;
  const body = `text-[10px] font-mono leading-relaxed ${dark ? 'text-gray-200' : 'text-gray-500'}`;

  return (
    <section className="w-full">
      <div
        className={`p-[15px] rounded-md mb-[12px] relative flex flex-col gap-6 ${description || tags?.length ? 'lg:min-h-[240px]' : 'pb-8'} ${
          dark ? 'bg-black text-white overflow-hidden isolate min-h-[260px]' : 'bg-white'
        }`}
      >
        {dark ? (
          // Behind the words but above the panel's own black: the panel isolates its stacking.
          <div className="absolute inset-0 -z-10" aria-hidden>
            {backdrop}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0) 65%), linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 30%)',
              }}
            />
          </div>
        ) : null}

        {/* What it is, and when: one line across the top. */}
        <div className="flex items-start justify-between gap-6 -mt-[7px] -mx-[7px]">
          <AnimatedTitle marginClass="mb-1">{leftTop}</AnimatedTitle>
          <AnimatedTitle marginClass="mb-1">{rightTop}</AnimatedTitle>
        </div>

        <div className="flex-1 flex flex-col justify-between gap-8 min-w-0">
          <div className="lg:pt-3">
            <h1 className={`text-[20px] md:text-[24px] leading-tight tracking-tight font-superior uppercase ${dark ? 'text-white' : 'text-black'}`}>{title}</h1>
            {subtitle ? <p className={`${body} uppercase mt-2 max-w-xl`}>{subtitle}</p> : null}
          </div>

          {description || tags?.length ? (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,32rem)_minmax(0,1fr)] gap-6 lg:gap-12 items-end">
              {description ? (
                <div>
                  <p className={label}>[ DESCRIPTION ]</p>
                  <p className={body}>{description}</p>
                </div>
              ) : null}
              {tags?.length ? (
                <div>
                  <p className={label}>[ TAGS ]</p>
                  <div className="flex flex-wrap gap-1 max-w-lg">
                    {tags.map((tag, index) => (
                      <span key={index} className="text-[10px] bg-gray-300 text-black px-2 py-1 rounded-md">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
